/**
 * Client-safe types and constants from schema.
 * This file contains ONLY types, constants, and helper functions that can be safely
 * imported by client-side code without pulling in drizzle-orm or database dependencies.
 */

import { z } from 'zod';

// ============================================================================
// USER TYPES
// ============================================================================

export const userRoleEnum = ['user', 'hr_admin', 'content_admin', 'super_admin'] as const;
export type UserRole = (typeof userRoleEnum)[number];

// ============================================================================
// AUTH SCHEMAS
// ============================================================================

export const signupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phoneNumber: z.string().optional(),
  companyCode: z.string().optional(),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(6),
});

// Auth types
export type SignupData = z.infer<typeof signupSchema>;
export type LoginData = z.infer<typeof loginSchema>;
export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

// ============================================================================
// HYDRATION MESSAGE BANK
// From PWA PROJECT MAP - Notif Bank.csv (timing / activity / health / milestone)
// Shared by home carousel and OneSignal push via getHydrationReminderMessage
// ============================================================================

export {
  HYDRATION_MESSAGES,
  NOTIF_BANK,
  TIMING_BANK,
  ACTIVITY_BANK,
  HEALTH_BANK,
  MILESTONE_BANK,
  getHydrationReminderMessage,
  getRotatingNotificationMessage,
  getTimingSlot,
  getLocalTimeParts,
  getActivityMessage,
  getHealthMessage,
  getMilestoneMessage,
  extractMessageIcon,
  type HydrationMessage,
  type NotifTrigger,
  type TimingSlot,
  type MilestoneKind,
  type NotifBankEntry,
  type SelectedNotifMessage,
} from './notifBank';

// ============================================================================
// END-OF-DAY BADGE SYSTEM
// Volume and Frequency badges: shared/badgeCalculation.ts
// Time badges remain here for mode display.
// ============================================================================

export {
  FREQUENCY_BADGES,
  VOLUME_BADGES,
  getFrequencyBadge,
  getVolumeBadge,
  calculateBadges,
  calculateCurrentExpectedVolume,
  calculateCurrentVolumePercentage,
  type FrequencyBadge,
  type FrequencyBadgeColor,
  type FrequencyBadgeId,
  type VolumeBadge,
  type VolumeBadgeColor,
  type BadgeCalculationInput,
  type BadgeCalculationResult,
  type DailyBadgeSnapshot,
} from './badgeCalculation';

// Time-Based Badges (Mode Names - based on active hours)
// Beast Mode removed — 12–16h uses Tank.
export const TIME_BADGES = [
  { minHours: 0, maxHours: 6, name: 'Spark', emoji: '⚡', description: 'Quick session' },
  { minHours: 6, maxHours: 8, name: 'Spark', emoji: '⚡', description: '6-8 hours' },
  { minHours: 8, maxHours: 10, name: 'Cruise', emoji: '🚗', description: '8-10 hours' },
  { minHours: 10, maxHours: 12, name: 'Standard', emoji: '📊', description: '10-12 hours' },
  { minHours: 12, maxHours: 16, name: 'Tank', emoji: '🛡️', description: '12-16 hours' },
  { minHours: 16, maxHours: 24, name: 'God', emoji: '👑', description: 'Always on' },
] as const;

export type TimeBadge = (typeof TIME_BADGES)[number];

export function getTimeBadge(activeHours: number): TimeBadge {
  for (let i = TIME_BADGES.length - 1; i >= 0; i--) {
    if (activeHours >= TIME_BADGES[i].minHours) {
      return TIME_BADGES[i];
    }
  }
  return TIME_BADGES[0];
}

// Combined end-of-day badge result
export interface EndOfDayBadges {
  frequency: import('./badgeCalculation').FrequencyBadge;
  volume: import('./badgeCalculation').VolumeBadge;
  time: TimeBadge;
  summary: string;
}
